import classNames from 'classnames';
import React, { ReactNode, useState } from 'react';
import { Logo } from '../../../../layout/BotBuilderSidebar/Logo/Logo';
import styles from './CustomerQualificationFlow.module.css';
import DannyGuthrieSrc from './images/danny-guthrie.png';
import RichDadSrc from './images/rich-dad.png';
import richDadLogoSrc from './images/rich-dad-logo.png';
import oneTextLogoSrc from './images/one-text-logo.png';
import { Iconiq } from '../LoginLayout/BlueLayout/Iconiq';
import { Inc500 } from '../LoginLayout/BlueLayout/Inc500';
import { G2BadgeTop50 } from '@mm/icons/general/G2BadgeTop50';
import { ReactComponent as G2 } from 'assets/g2.svg';
import { MinusOutlined } from '@ant-design/icons';
import PrimarySelect, {
  DropdownOption,
} from '../../../../common/components/PrimarySelect/PrimarySelect';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { useSelector } from 'react-redux';
import TextInput from '../../../../common/components/TextInput';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';
import { OnboardingRoutes } from '../../../../routes/onboarding-routes';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { useHistory } from 'react-router-dom';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { Button } from '@mm/components';
import { Routes } from '../../../../routes/top-level-routes';

export const CustomerQualificationLayout = ({
  children,
  step,
}: {
  children: ReactNode;
  step: number;
}) => {
  const activeCompany = useSelector(activeCompanySelector);
  const { id: userId } = useSelector(selectSessionSelector) || {};
  const [error, setError] = useState(false);
  const [websiteTraffic, setWebsiteTraffic] = useState<
    DropdownOption | undefined
  >(undefined);
  const [adSpend, setAdSpend] = useState<DropdownOption | undefined>(undefined);
  const [agencyClientsInput, setAgencyClientsInput] = useState('');
  const [busy, setBusy] = useState(false);

  const isAgency = activeCompany?.businessType === 'agency';
  const submitDisabled =
    !adSpend || (isAgency ? !agencyClientsInput.length : !websiteTraffic);

  const { update } = useCompanyCRUDActions();
  const { updateUserAttributes } = useUsersCRUDActions();
  const history = useHistory();

  const websiteVisitorsOptions = [
    { id: '0-10000', name: '0 - 10,000 visitors' },
    { id: '10001-20000', name: '10,001 - 20,000 visitors' },
    { id: '20001-50000', name: '20,001 - 50,000 visitors' },
    { id: '50001-99999', name: '50,000+ visitors' },
  ] as DropdownOption[];

  const adSpendOptions = [
    { id: '0-10000', name: '$0 - 10,000' },
    { id: '10001-20000', name: '$10,001 - 20,000' },
    { id: '20001-50000', name: '$20,001 - 50,000' },
    { id: '50001-99999', name: '$50,000+' },
  ] as DropdownOption[];

  const updateCompany = async () => {
    if (activeCompany) {
      const { id } = activeCompany;

      if (!submitDisabled) {
        setBusy(true);
        await update(
          {
            id,
            ...(adSpend && { monthlyAdSpend: adSpend?.id.toString() }),
            ...(websiteTraffic && {
              websiteVisitors: websiteTraffic?.id.toString(),
            }),
            ...(agencyClientsInput && {
              numberOfAgencyClients: agencyClientsInput,
            }),
          },
          'full=true',
        );

        await updateUserAttributes(id, userId, {
          onboardingSignupStep: activeCompany.consideredQualified
            ? OnboardingSignupStep.BOOK_DEMO
            : OnboardingSignupStep.NONE,
        });
        setBusy(false);

        if (activeCompany.consideredQualified) {
          history.push(OnboardingRoutes.bookDemo.path);
        } else {
          history.push(Routes.chatbotEditor.path);
        }
      }
    }
    setError(true);
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.whiteContainer]: step === 3,
      })}
    >
      <div
        className={classNames(styles.leftSide, {
          [styles.thankYouLeftSide]: step === 3,
        })}
      >
        <Logo className={styles.logo} />
        {step !== 3 && (
          <>
            <div className={styles.steps}>
              <span
                className={classNames(styles.step, {
                  [styles.activeStep]: step === 1,
                })}
              >
                Step 1
              </span>
              <MinusOutlined className={styles.tail} />
              <span
                className={classNames(styles.step, {
                  [styles.activeStep]: step === 2,
                })}
              >
                Step 2
              </span>
            </div>
            <div className={styles.formHeader}>
              <div className={styles.title}>
                Book a call to start your free trial
              </div>
              <div className={styles.subTitle}>
                Get a personalized walkthrough and consultation to see how
                Customers.ai can drive your business growth
              </div>
            </div>
          </>
        )}

        {step === 1 && children}
        {step === 2 && (
          <div className={styles.form}>
            {!isAgency && (
              <div className={styles.inputGroup}>
                <label
                  className={classNames(styles.formLabel, {
                    [styles.formLabelInvalid]: error && !websiteTraffic,
                  })}
                >
                  Monthly U.S. website visitors
                </label>
                <PrimarySelect
                  className={classNames(styles.formSelect)}
                  value={websiteTraffic}
                  onChange={setWebsiteTraffic}
                  options={websiteVisitorsOptions}
                />
              </div>
            )}
            <div className={styles.inputGroup}>
              <label
                className={classNames(styles.formLabel, {
                  [styles.formLabelInvalid]: error && !adSpend,
                })}
              >
                {isAgency
                  ? 'Monthly managed client ad spend'
                  : 'Monthly ad spend'}
              </label>
              <PrimarySelect
                className={classNames(styles.formSelect)}
                value={adSpend}
                onChange={setAdSpend}
                options={adSpendOptions}
              />
            </div>

            {isAgency && (
              <div className={styles.inputGroup}>
                <label
                  className={classNames(styles.formLabel, {
                    [styles.formLabelInvalid]:
                      error && !agencyClientsInput.length,
                  })}
                >
                  How many of your clients are a good fit for visitor ID &
                  remarketing?
                </label>
                <TextInput
                  className={classNames(styles.formInput)}
                  onChange={e => setAgencyClientsInput(e.target.value)}
                  aria-label="Clients"
                  value={agencyClientsInput}
                  type="text"
                />
              </div>
            )}

            <Button
              css={{ container: styles.submitBtn }}
              disabled={submitDisabled || busy}
              onClick={updateCompany}
            >
              Book a Call
            </Button>
          </div>
        )}
      </div>
      <div className={styles.rightSide}>
        <div className={styles.partner}>
          <img src={RichDadSrc} alt="Rich Dad" className={styles.partnerImg} />
          <div className={styles.partnerDescription}>
            <img
              src={richDadLogoSrc}
              alt="Rich Dad logo"
              className={styles.partnerLogo}
            />
            <div className={styles.quote}>
              “Customers.ai performs much better than our old tools. We
              struggled at first with 14-15% email open rates and it took a lot
              of effort to get to 32%. With Customers.ai we're seeing 65-85%
              open rates… and it saves me a ton of time.”
            </div>
          </div>
        </div>
        <div className={styles.partner}>
          <img
            src={DannyGuthrieSrc}
            alt="Danny Guthrie"
            className={styles.partnerImg}
          />
          <div className={styles.partnerDescription}>
            <div>
              <div className={styles.name}>Danny Guthrie</div>
              <div className={styles.role}>Sales Team Lead</div>
            </div>
            <img
              src={oneTextLogoSrc}
              alt="OneText logo"
              className={styles.partnerLogo}
            />
            <div className={styles.quote}>
              “We used Customers.ai to improve lead attribution and fill the
              sales funnel. In just 30 days, we were able to capture 1400 new
              leads and add over $70,000 in MRR to the sales pipeline.”
            </div>
          </div>
        </div>
        <div className={styles.badges}>
          <Iconiq />
          <G2BadgeTop50 />
          <G2 style={{ width: '90px', marginTop: '-10px', minWidth: '90px' }} />
          <Inc500 style={{ marginLeft: '-10px' }} />
        </div>
      </div>
    </div>
  );
};
